import React, { useState, useEffect } from 'react';
import {
  Box,
  Select,
  FormControl,
  FormLabel,
  HStack,
  Text,
  Badge,
  Spinner,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { FiMapPin, FiFilter } from 'react-icons/fi';
import { useUserContext } from '../context/UserContext';
import { useFetchBranches } from '../services/companyService';

const BranchFilter = ({ 
  selectedBranch, 
  onBranchChange, 
  showLabel = true, 
  placeholder = "All Branches",
  size = "md",
  maxW = "250px",
  isDisabled = false,
  showBranchCount = false,
  variant = "outline"
}) => {
  const { user } = useUserContext();
  const [localSelectedBranch, setLocalSelectedBranch] = useState(selectedBranch || 'all');

  // Fetch branches for company admins
  const { 
    branches: branchesData, 
    branchesIsLoading, 
    branchesError 
  } = useFetchBranches({
    enabled: user?.role === 'company_admin'
  });

  // Update local state when prop changes
  useEffect(() => {
    setLocalSelectedBranch(selectedBranch || 'all');
  }, [selectedBranch]);

  // Handle branch selection change
  const handleBranchChange = (branchId) => {
    setLocalSelectedBranch(branchId);
    if (onBranchChange) {
      onBranchChange(branchId);
    }
  };



  // Only show for company admins (branch users don't need branch filtering)
  if (user?.role !== 'company_admin') {
    return null;
  }

  // Show loading state
  if (branchesIsLoading) {
    return (
      <Box>
        {showLabel && <FormLabel fontSize="sm" color="gray.600">Branch Filter</FormLabel>}
        <HStack spacing={2}>
          <Spinner size="sm" color="blue.500" />
          <Text fontSize="sm" color="gray.500">Loading branches...</Text>
        </HStack>
      </Box>
    );
  }

  // Show error state
  if (branchesError) {
    return (
      <Box>
        {showLabel && <FormLabel fontSize="sm" color="gray.600">Branch Filter</FormLabel>}
        <Alert status="error" size="sm" borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">Failed to load branches</Text>
        </Alert>
      </Box>
    );
  }

  // Get branches array
  const branches = Array.isArray(branchesData) ? branchesData : [];

  return (
    <Box>
      {showLabel && (
        <FormLabel fontSize="sm" color="gray.600" mb={2}>
          <HStack spacing={2}>
            <FiMapPin size={14} />
            <Text>Branch Filter</Text>
            {showBranchCount && branches.length > 0 && (
              <Badge colorScheme="blue" variant="subtle" fontSize="xs">
                {branches.length} branches
              </Badge>
            )}
          </HStack>
        </FormLabel>
      )}
      
      <FormControl>
        <Select
          value={localSelectedBranch}
          onChange={(e) => handleBranchChange(e.target.value)}
          placeholder={placeholder}
          size={size}
          maxW={maxW}
          isDisabled={isDisabled || branchesIsLoading}
          variant={variant}
          icon={<FiFilter size={14} />}
        >
          <option value="all">All Branches</option>
          {branches.map((branch) => (
            <option key={branch.branch_id} value={branch.branch_id}>
              {branch.name}
            </option>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default BranchFilter; 